// What a face-swap batch takes from the balance, as the server charges it
// (FaceSwitchingBatchBalance; CoreCredits::Allocator, one reservation per
// task): each task is charged on its own, credit types paying in the policy's
// order at their multiplier, so free credits count double. Free credits pay
// for a clip (video or GIF) only while the account's free clip is left and the
// clip is maxSeconds or less; the first clip they actually pay for spends it.
// A longer clip, or one of unknown length (a duration of 0, NaN or Infinity),
// is paid without free credits; when the other credit types can't pay for all
// of it and the free clip is left, it's trimmed to its first maxSeconds
// instead, priced at trimmedCents and paid with free credits alone.
// Tasks are { baseCents, clip, durationSeconds }; amounts are cents. Returns
// the total, the extra each credit type adds over the base cost, how many clips
// free credits weren't allowed to pay for, which tasks are trimmed (indexes),
// and what the wallet can't cover (uncovered, counted in the total at face
// value: the server refuses the batch).
// test/lib/face_swap_charge_parity_test.rb checks it against the server.
export function chargeTasks(tasks, availableCents = {}, policy = {}, freeVideo = {}) {
  const available = { ...availableCents }
  const { priority = [], multipliers = {} } = policy
  const withoutFree = priority.filter(type => type !== "free")
  const freeOnly = priority.filter(type => type === "free")
  const maxSeconds = freeVideo.maxSeconds ?? 30
  let clipsLeft = freeVideo.remaining ?? 0
  const extra = {}
  const trimmed = []
  let total = 0
  let clipsRefused = 0
  let uncovered = 0

  const multiplier = type => Number(multipliers[type] ?? 1)
  const coverable = type => Math.floor((available[type] || 0) / multiplier(type))

  tasks.forEach(({ baseCents, clip = false, durationSeconds = 0 }, index) => {
    let remaining = baseCents
    let types = priority
    const knownLength = Number.isFinite(durationSeconds) && durationSeconds > 0
    if (clip && !(clipsLeft > 0 && knownLength && durationSeconds <= maxSeconds)) {
      const paidCovers = withoutFree.reduce((sum, type) => sum + coverable(type), 0) >= baseCents
      if (clipsLeft > 0 && !paidCovers) {
        trimmed.push(index)
        remaining = freeVideo.trimmedCents
        types = freeOnly
      } else {
        clipsRefused += 1
        types = withoutFree
      }
    }
    let freeCharged = 0
    for (const type of types) {
      if (remaining === 0) break
      const base = Math.min(remaining, coverable(type))
      if (base <= 0) continue

      const spendable = available[type] || 0
      const charged = Math.min(Math.ceil(base * multiplier(type)), spendable)
      available[type] = spendable - charged
      extra[type] = (extra[type] || 0) + charged - base
      if (type === "free") freeCharged += charged
      total += charged
      remaining -= base
    }
    if (clip && freeCharged > 0) clipsLeft -= 1
    total += remaining
    uncovered += remaining
  })

  return { total, extra, clipsRefused, trimmed, uncovered }
}

// Whether the bill says the free tier doesn't cover the batch's clips: only
// when the batch won't go through and free credits were held back from a clip,
// not when purchased or referral credits pay for it.
export function freeVideoNoteShown({ clipsRefused, uncovered }, availableCents = {}) {
  return (availableCents.free || 0) > 0 && clipsRefused > 0 && uncovered > 0
}

// Whether the form says a clip will be swapped trimmed: only when the batch
// goes through.
export function freeVideoTrimNoteShown({ trimmed, uncovered }) {
  return trimmed.length > 0 && uncovered === 0
}
